/**
 * TypeScript mirror of the public engine contract (OpenAPI 1.10.0 —
 * docs/specs/tokenforge-v1-engine-openapi.yaml). These describe ONLY what the
 * documented surface returns; the moat-allowlist test on the server guarantees
 * nothing else ever appears, so the SPA never has to defend against extra fields.
 */

export const ENGINE_CONTRACT_VERSION = '1.10.0';

// ── catalogue ───────────────────────────────────────────────────────────────

export type Mm = number;
export type PointMm = [Mm, Mm];

/** Raw cut-outline path + centroid transform for native <path> rendering. */
export interface ShapeOutline {
    d: string;
    cx: number;
    cy: number;
    scale: number;
}

export interface CatalogueShape {
    key: string;
    displayName: string;
    /** Server-flattened safe-margin polygon (shape-centroid mm). [] ⇒ unknown. */
    safeMarginMm: PointMm[];
    outline: ShapeOutline | null;
    /**
     * PROPOSED (1.14.0, asked of the back end 10-26; read only through core/size.ts): the cut
     * outline's width × height, as stated by the server from the cut geometry. Absent = the size
     * isn't shown anywhere (never estimated here).
     */
    cutSizeMm?: { w: number; h: number } | null;
    /** 1.20.0: whether a design may choose a size on this shape. Absent (older engine) = not offered. */
    sizable?: boolean;
    /** 1.20.0: the smallest size a design may choose on this shape. */
    minSizeMm?: number;
}

export interface CatalogueFont {
    key: string;
    displayName: string;
    /** CSS font-weight / font-style for the preview. */
    weight: string;
    style: string;
    /** sha256 of the binary — verified before the shaper reads it; versions the URL. */
    sha256: string;
    avgCharAspect: number | null;
    /** R17 — contract 1.6.0. Absent/null = show nothing. */
    credit?: FontCredit | null;
    /** 1.16.0: the picker's group (`fantasy`, `sci_fi`, …), or null; absent before. */
    category?: string | null;
    /** 1.16.0: the font's name drawn in itself by the server (mm, `x y w h` = its box), so the picker needs no font file. */
    preview?: FontPreviewPath | null;
}

/** 1.16.0: a server-drawn picture of a font's name. */
export interface FontPreviewPath {
    d: string;
    x: number;
    y: number;
    w: number;
    h: number;
}

/**
 * R17 — contract 1.6.0. One pre-formatted line from token_font.license_attribution
 * (e.g. "Montserrat © … licensed under SIL Open Font License 1.1. Source: …"),
 * shown as is; the back end does not split it into fields.
 */
export interface FontCredit {
    /** e.g. `open_source`; the back end's enum value. */
    licenseType: string;
    attributionText: string;
}

export interface CatalogueIcon {
    key: string;
    displayName: string;
    /** Raw SVG path data, verbatim — flattened client-side with the CORE flattener. */
    pathD: string;
    bbox: { x: number; y: number; w: number; h: number };
    /** R17 — contract 1.6.0. Absent/null = show nothing. */
    credit?: IconCredit | null;
}

/**
 * R17 — contract 1.6.0. CC BY 3.0 requires credit to the author and a link to the
 * licence (docs/operations/game-icons-net-legal-review.md §2), so these come from
 * IconAsset's license* columns and the SPA shows them wherever an icon is chosen.
 */
export interface IconCredit {
    /** e.g. `cc_by_3_0`, `cc0`; the back end's enum value. */
    licenseType: string;
    author: string | null;
    authorUrl: string | null;
    licenseUrl: string | null;
    /** Pre-formatted credit line; preferred over composing one when present. */
    attributionText: string | null;
}

/** One row of the customer-facing volume-pricing ladder (1.2.0). */
export interface PriceBreak {
    minQty: number;
    /** Display-only headline rate; NOT a reconstruction key for the charge. */
    unitPriceCents: number;
    /** Authoritative sticker price for an order of exactly `minQty`. */
    packPriceCents: number;
}

/** 1.18.0: one size tier of the price table; tokens up to `maxMm` (longest side) are priced in it. */
export interface PricingTier {
    key: string;
    maxMm: number;
    /** How the tier is cut, which sets its minimum charge. */
    cut: 'strip' | 'sheet';
}

/**
 * 1.22.0 'sheet' (docs/plans/tokenforge-sheet-share-pricing-2026-11-02.md §4; Ken 11-02): a color's tokens share a half
 * sheet at `firstSheetCents`, then `afterSheetCents` for each further sheet's worth; every batch is at least
 * `minimumCents`. Read via core/sheetPricing.ts. The server prices; these give the designer "about" figures only.
 */
export interface SheetPricingDisclosure {
    minimumCents: number;
    firstSheetCents: number;
    afterSheetCents: number;
    /** Tokens up to this size (longest side, inclusive) are cut on the jig; larger ones on the flat sheet. */
    jigMaxMm: number;
    /** Published tokens per half sheet, by shape key then size in mm as a string ("18", "18.5"). Missing = none published. */
    capacity: Record<string, Record<string, number>>;
    /** 1.29.0, only when the table splits cutting from engraving (Ken 11-04): the cutting share, 3100 = 31%. */
    cutShareBasisPoints?: number;
    /** 1.29.0, with the split: the reference sheet's cut line, in mm. */
    referenceCutMm?: number;
    /** 1.29.0, with the split: shape key → mm of cut line per mm of size (a circle is π). */
    cutMmPerMm?: Record<string, number>;
}

/** Static, cacheable pricing structure for the buying UI (1.2.0; table fields 1.18.0, read via core/tablePricing.ts). */
export interface PricingDisclosure {
    minimumQuantity: number;
    quantityStep: number;
    /** Ascending by minQty. The highest break ≤ qty sets the per-unit rate. The pack ladder; absent under 'table'. */
    breaks: PriceBreak[];
    /** 1.18.0: where prices come from. Absent = an older engine = 'ladder'. 1.22.0: 'sheet'. */
    source?: 'ladder' | 'table' | 'sheet';
    /** 1.22.0 'sheet': the rates and the capacity table. */
    sheet?: SheetPricingDisclosure;
    /** 1.18.0: the published table's version (also in every price answer). */
    version?: number;
    /** 1.18.0 'table': ascending; a token is priced in the first tier whose maxMm ≥ its longest side. */
    tiers?: PricingTier[];
    /** 1.18.0 'table': the quantity rows; a material's count uses the highest row ≤ it (below the first, the first). */
    rows?: number[];
    /** 1.18.0 'table': cents per token, per tier, aligned to `rows`. */
    perToken?: Record<string, number[]>;
    /** 1.18.0 'table': the minimum charge per material, by cut class (cents). */
    minimums?: { strip: number; sheet: number };
    /** 1.21.0 'table': the minimum charge per material, by tier key (cents); null on an incomplete table. Over `minimums`, which then holds each cut's highest tier. */
    minimumsByTier?: Record<string, number | null>;
    /** 1.18.0 'table': larger tokens are not sold. */
    notSoldAboveMm?: number;
    /** 1.20.0 'table': the smallest size a design may choose. */
    minSizeMm?: number;
}

/** Customer-facing finish family — drives the swatch sections. */
export type MaterialFinishGroup = 'fluorescent' | 'translucent' | 'transparent' | 'opaque';

/** How clearly the engraving shows on a color (Ken 11-02). */
export type EngravingClarity = 'excellent' | 'average' | 'poor';

/** One material/color swatch (substrate-as-color) offered for this product (1.3.0). */
export interface MaterialOption {
    /** Ken 11-02: how clearly the engraving shows; absent from an older engine (nothing is shown then). */
    engravingClarity?: EngravingClarity;
    key: string;
    displayName: string;
    finishGroup: MaterialFinishGroup;
    /** `#RRGGBB`; swatch chip + on-canvas token fill. */
    colorHex: string;
    /** 100 = opaque; lower = more see-through. */
    opacityPct: number;
    /** Fluorescent halo on canvas. */
    glow: boolean;
    /**
     * PROPOSED (1.14.0, read only through core/size.ts): the sheet's nominal thickness, per
     * material because not every stock is 1/8 in. Absent = not shown.
     */
    thickness?: { nominalMm: number; label: string } | null;
}

export interface Catalogue {
    customItemTypeKey: string;
    shapes: CatalogueShape[];
    fonts: CatalogueFont[];
    icons: {
        total: number;
        offset: number;
        limit: number;
        /** 1.15.0: the first `featuredCount` icons of the full list are the featured ones (Ken 10-28); absent before. */
        featuredCount?: number;
        items: CatalogueIcon[];
    };
    pricing: PricingDisclosure;
    materials: MaterialOption[];
    /**
     * 1.11.0: which switched features are on right now. `saveShare` is the save + share kill
     * switch (true only while it is on). The catalogue is cached up to ~6 minutes, so the routes
     * may already answer 503 while this still says true.
     */
    features?: { saveShare?: boolean };
    /** 1.20.0: production constants the designer draws with. `safeMarginMm`: the safe zone's distance inside the cut. */
    prepress?: { safeMarginMm: number };
    /** 1.23.0: the size (whole mm) a new design starts at, product-wide (Ken 11-02, R21). Absent before. */
    defaultSizeMm?: number;
    /** 1.24.0: the most designs one proof (and so one pack) can show. Absent before; the server's cap was 12 throughout. */
    maxProofDesigns?: number;
}

/** 1.15.0: one page of `GET /v1/engine/icons` (search by name, key or tag). */
export interface IconSearchResult {
    customItemTypeKey: string;
    query: string;
    total: number;
    offset: number;
    limit: number;
    items: CatalogueIcon[];
}

// ── validate-and-price ────────────────────────────────────────────────────────

export interface ValidationFinding {
    code: string;
    severity: 'error' | 'warning';
    message: string;
    path: string;
}

export interface PricingResult {
    unitPriceCents: number;
    /** The real charge, including the review fee (under 'table': breakdown.totalCents + professionalReviewFeeCents). */
    totalPriceCents: number;
    professionalReviewFeeCents: number;
    currency: string;
    /** 1.18.0: what priced this answer. Differing from the catalogue's `pricing.source` = prices changed mid-session. */
    pricingSource?: 'ladder' | 'table' | 'sheet';
    /** 1.18.0: the published table's version under 'table'; null under 'ladder'. */
    priceTableVersion?: number | null;
    /** 1.18.0, under 'table' only: how the pack was priced (excludes the review fee). */
    breakdown?: PriceBreakdown | null;
}

/** 1.18.0: one material's charge in a pack, as the server worked it out. */
export interface PriceBreakdownLine {
    material: string;
    tokens: number;
    tier: string;
    row: number;
    perTokenCents: number;
    subtotalCents: number;
    minimumCents: number;
    /** What the minimum charge adds on top of the subtotal (0 when the subtotal reaches it). */
    topUpCents: number;
    /** The cut class whose minimum applies (one per material: the sheet's when any token needs a sheet). */
    cut?: 'strip' | 'sheet';
    /** The next row up and what the material would cost there; null at the last row. */
    next: { row: number; addTokens: number; subtotalCents: number } | null;
}

/** 1.18.0: how a pack was priced. */
export interface TablePriceBreakdown {
    source: 'ladder' | 'table';
    version: number;
    lines: PriceBreakdownLine[];
    totalCents: number;
}

/** 1.22.0: one physical batch (half sheet) of a color group. */
export interface SheetBatch {
    /** 0-based. */
    sheetIndex: number;
    lineCents: number;
    /** Tokens this batch still covers at no extra cost: 0 once spent, never negative. */
    roomFor: number;
}

/** 1.22.0: one shape at one size in one color. */
export interface SheetBlock {
    shape: string;
    sizeMm: number;
    tokens: number;
    /** The `tokenDesigns` indices this block prices. */
    designs: number[];
    /** Display only, rounded half up: the first batch's price per token. */
    perTokenCents: number;
    /** Display only: the price per token after `breakAt` tokens. */
    afterBreakPerTokenCents: number;
    breakAt: number;
    roomFor: number;
    /** True when the share comes from the shelf estimate (a second shape or size in the batch): its figures are "about". */
    estimated: boolean;
}

/** 1.22.0: one color on one kind of sheet (jig or flat); each of its batches costs at least the minimum. */
export interface SheetGroup {
    material: string;
    sheet: 'jig' | 'flat';
    minimumCents: number;
    exactCents: number;
    /** Half sheets used, summed over its blocks; may exceed 1. */
    shareUsed: number;
    lineCents: number;
    sheets: SheetBatch[];
    blocks: SheetBlock[];
}

/** 1.22.0: how a pack was priced under 'sheet'. */
export interface SheetPriceBreakdown {
    source: 'sheet';
    version: number;
    groups: SheetGroup[];
    totalCents: number;
}

export type PriceBreakdown = TablePriceBreakdown | SheetPriceBreakdown;

export interface ValidateAndPriceResponse {
    validation: { ok: boolean; findings: ValidationFinding[] };
    pricing: PricingResult | null;
    canonicalSpec: Record<string, unknown>;
    derivedReferences: { retiredReferences: string[] };
}


// ── proof (P1, contract 1.8.0) ─────────────────────────────────────────────────
//
// docs/plans/tokenforge-p1b-build-spec.md (tokenforge-backend). The proof is keyed
// by the design token (dvk_…); start answers `ready` in the common case.

export type ProofStatus = 'pending' | 'ready' | 'failed';

/** Why a proof failed; the SPA owns the customer copy for each. */
export type ProofFailureCode = 'font_unresolved' | 'reference_missing' | 'shaping_failed' | 'render_retries_exhausted' | 'bend_out_of_range'
    /** 1.24.0: more designs than one proof shows (the catalogue's `maxProofDesigns`). */
    | 'too_many_designs';

/** The proof's status block, returned by start (A) and by every poll (B). */
export interface ProofState {
    designToken: string;
    status: ProofStatus;
    /** Watermarked PNG (absolute, `?v=<sha>`), null until `ready`. */
    proofUrl: string | null;
    /** sha256 of the PNG at `proofUrl`; sent back on approve to bind it to what was shown. */
    proofSha256: string | null;
    /** R18 pack code the customer quotes to identify or reorder this pack. */
    reorderCode: string;
    /** ISO instant; approving after it is a 409 `price_expired`. */
    priceValidUntil: string;
    /** R16 switch: may the customer add to cart while the proof is still pending? */
    pendingCheckoutAllowed: boolean;
    /** How long the SPA waits for `ready` before offering the pending add to cart (0 when ready). */
    waitBudgetSeconds: number;
    /** Set when `status === 'failed'`; an unknown code gets generic copy. */
    failureCode: string | null;
    /**
     * A mixed pack's proof (schema 1.2, tokenforge-backend B3): its real-size pages in the order to show them (pages with
     * a flagged design first). Present on every 1.2 proof, even one page; absent on 1.0/1.1, which keep `proofUrl`.
     */
    pages?: ProofPage[];
    /** 1.2: every design in PACK order, with the page it is on and its flags. */
    designs?: ProofDesign[];
}

/** One real-size proof page (B3). Its `sha256` is stable for unchanged content: no page number or code in the image. */
export interface ProofPage {
    url: string;
    sha256: string;
    designIds: string[];
}

/** One design of a 1.2 proof (B3). */
export interface ProofDesign {
    id: string;
    /** Index into `pages`. */
    page: number;
    changedSinceLastProof: boolean;
    /** Engraving hard to read on this colour. */
    faint: boolean;
}

/** A's wire shape: validation and pricing, with the status block flat (all null on a finding). */
export type ProofStartWire = {
    validation: { ok: boolean; findings: ValidationFinding[] };
    pricing: PricingResult | null;
} & { [K in keyof ProofState]: ProofState[K] | null };

/** A as the SPA uses it: the status block is null on a validation finding (nothing frozen). */
export interface ProofStartResponse {
    validation: { ok: boolean; findings: ValidationFinding[] };
    pricing: PricingResult | null;
    /** Null on a validation finding — nothing was frozen. */
    proof: ProofState | null;
}

export interface ProofApproveResponse {
    cartUrl: string;
    sku: string;
    designToken: string;
    /** R18 pack code, also in the Shopify product title. */
    reorderCode: string | null;
    /**
     * Only on a pending approve: the late-proof approval page
     * (`https://design.litko.net/approve#<secret>`). Handed out once — a repeat
     * approve answers null, the server keeps only the secret's hash.
     */
    proofApprovalUrl?: string | null;
    /** 1.14.0: the saved design made by `saveToMyTokens`; null (or absent) when nothing was saved. */
    savedTokenKey?: string | null;
}

// ── late-proof approval page (Ken, 10-20; contract 1.8.0) ───────────────────────

/**
 * Where one pack stands on the approval page. 1.13.0 (fix flow): `waiting_fix` is a fix we made that waits for the
 * customer's approval (confirmed like `waiting`); `superseded` is a fix a newer one replaced, never confirmable (the
 * newer one has its own link, which this page is never given).
 */
export type ApprovalPackState = 'waiting' | 'waiting_fix' | 'superseded' | 'confirmed' | 'closed';

/** Why we fixed a design (a closed list; the email carries only this). */
export type FixReason = 'safe_edge' | 'spelling' | 'image_clarity' | 'other';

/** One pack the approval link names. */
export interface ApprovalPack {
    designToken: string;
    reorderCode: string | null;
    status: ProofStatus;
    proofUrl: string | null;
    proofSha256: string | null;
    failureCode: string | null;
    confirmedAt: string | null;
    /** When the customer said "Something's wrong" about this proof (asked of the back end; absent until it ships). */
    reportedAt?: string | null;
    state: ApprovalPackState;
    customItemTypeKey: string | null;
    orderQuantity: number;
    /** The canonical spec as frozen; the page lists its words from it. */
    designSpec: unknown;
    // ── 1.13.0, the fix flow; absent from an older engine ──
    /** 1 = the design as ordered; 2+ = a fix. */
    revision?: number;
    fixReason?: FixReason | null;
    /** The reason in the customer's words, the fix email's own sentence; null unless it is a fix. */
    fixReasonText?: string | null;
    /** Our designer's note, ≤280 characters, PLAIN TEXT: only ever rendered as a text node. */
    fixNote?: string | null;
    /** The proof this fix replaces, for before/after; null unless it is a fix. */
    previousProofUrl?: string | null;
}

/** `POST /v1/engine/approvals/read`: the order (after payment) and its packs, or the one pack (before). */
export interface ApprovalRead {
    orderName: string | null;
    packs: ApprovalPack[];
}

export interface ApprovalConfirmResult {
    designToken: string;
    confirmedAt: string | null;
    state: ApprovalPackState;
}

export interface ApprovalReportResult {
    designToken: string;
    reportedAt: string;
}

// ── reopen a pack (change quantity; the slice after P1b) ─────────────────────────

/**
 * E: `GET /v1/engine/designs/{designToken}` — a cart-bound, unpaid pack within
 * its 30 days, read back so the studio can reopen it. 404 otherwise.
 */
export interface DesignRead {
    designToken: string;
    customItemTypeKey: string;
    orderQuantity: number;
    designSpec: unknown;
    reorderCode: string;
    /** 1.27.0: the pack's name on the cart line, to keep through Change quantity; absent before. */
    customerLabel?: string | null;
}

// ── per-order credits (R17, contract 1.6.0) ─────────────────────────────────────

/** One icon's credit as frozen with the design at add to cart. Every field may be null. */
export interface FrozenIconCredit {
    displayName: string | null;
    licenseType: string | null;
    author: string | null;
    authorUrl: string | null;
    licenseUrl: string | null;
    attributionText: string | null;
    /** e.g. "Modified to fit the token shape." */
    modification: string | null;
}

export interface FrozenFontCredit {
    displayName: string | null;
    licenseType: string | null;
    attributionText: string | null;
}

/**
 * `GET /v1/engine/credits/{designToken}`. `recorded: false` = frozen before
 * credits were recorded, so the lists are empty and the page says the credits are
 * unavailable, not that there are none. Credits only: never the spec.
 */
export interface DesignCredits {
    designToken: string;
    recorded: boolean;
    icons: FrozenIconCredit[];
    fonts: FrozenFontCredit[];
}

// ── error envelope ────────────────────────────────────────────────────────────

export type EngineErrorCode =
    | 'malformed_request'
    | 'unsupported_schema_version'
    | 'request_too_large'
    | 'not_found'
    | 'rate_limited'
    | 'service_unavailable'
    | 'server_error'
    // 1.5.0: the Idempotency-Key was already used for a different body.
    | 'idempotency_key_reused'
    // 1.8.0 proof flow.
    | 'proof_not_ready'
    | 'price_expired'
    | 'proof_failed'
    | 'proof_mismatch'
    // Another approve of this pack is making the product: resend the same approve shortly.
    | 'approve_in_progress'
    | 'busy'
    // Approval page: a newer link replaced this one / the order was cancelled.
    | 'link_revoked'
    | 'order_closed'
    // 1.13.0 fix flow: a newer fix replaced this one; re-read the page.
    | 'superseded'
    // 1.9.0: a pending approve whose canvas shaper build isn't the server's — wait for the drawn proof.
    | 'proof_wait_required'
    // 1.11.0 save + share.
    | 'owner_required'
    | 'precondition_failed'
    | 'precondition_required'
    | 'quota_exceeded'
    | 'turnstile_failed'
    | 'share_revoked'
    | 'share_taken_down'
    | 'encryption_unavailable'
    // 1.25.0: the pack name is over 40 characters after the server's cleanup.
    | 'customer_label_invalid'
    // 1.26.0 keep designs: every refusal of POST /owners/link.
    | 'link_refused';

export interface EngineErrorEnvelope {
    error: { code: EngineErrorCode; message: string; requestId?: string };
}
